import React from "react";

import { PrescreeningSettings } from "manager/components/Prescreening";
import {
  useCopyPrescreeningDealSetupFromPortfolio,
  useResetPropertyVeroGoToPortfolioSettings,
  useResetToPrescreeningPortfolioSettings,
} from "manager/hooks/api";
import { PropertySettings } from "manager/interfaces/api";
import Banner from "shared/components/Banner";
import Icon from "shared/components/Icon";
import ConfirmationModal from "shared/components/Modals/ConfirmationModal";
import { usePermissions } from "shared/hooks";
import useModal from "shared/hooks/useModal";

import { openVeroGoOverrideDialog } from "../../VeroGoSettings/components/shared";

interface PropertyPrescreeningSettingsProps {
  propertySettings: PropertySettings;
}

const PropertyPrescreeningSettings = ({
  propertySettings,
}: PropertyPrescreeningSettingsProps) => {
  const { openModalDialog } = useModal();
  const { isVeroGoEnabled } = usePermissions();
  const propertySettingsVeroGo = propertySettings as any;

  const propertyId = propertySettings?.id;
  const propertyName = propertySettings?.name;
  const portfolioName = propertySettings?.portfolioName;
  const propertyVeroGoActive = Boolean(propertySettingsVeroGo?.veroGoActive);
  const isVeroGoFromPortfolio = Boolean(
    propertySettingsVeroGo?.isVeroGoFromPortfolio
  );
  const portfolioVeroGoActive = isVeroGoFromPortfolio
    ? propertyVeroGoActive
    : !propertyVeroGoActive;
  const shouldUseVeroGoReset =
    isVeroGoEnabled && (propertyVeroGoActive || portfolioVeroGoActive);
  const hasVeroGoMismatch = !isVeroGoFromPortfolio;
  const willEnableVeroGoOnReset =
    shouldUseVeroGoReset && !propertyVeroGoActive && portfolioVeroGoActive;
  const isVeroGoActiveForProperty = isVeroGoEnabled && propertyVeroGoActive;
  const isPrescreeningDealSetupFromPortfolio =
    propertySettings?.isPrescreeningDealSetupFromPortfolio;

  const { copyPrescreeningDealSetupFromPortfolio } =
    useCopyPrescreeningDealSetupFromPortfolio();

  const { resetToPrescreeningPortfolioSettings } =
    useResetToPrescreeningPortfolioSettings();
  const { resetPropertyVeroGoToPortfolioSettings } =
    useResetPropertyVeroGoToPortfolioSettings();

  const openCustomizeSettingsModal = () => {
    const veroGoDescription = isVeroGoActiveForProperty ? (
      <>
        <strong>Warning:</strong> With Vero Go enabled, updating pre-screening
        settings here will also decouple screening settings for this property.
      </>
    ) : undefined;

    // @ts-ignore
    return openModalDialog(ConfirmationModal, {
      title: "You're going to update property settings",
      subtitle: (
        <span>
          Property: <strong>{propertyName}</strong>
        </span>
      ),
      submitButtonLabel: "Confirm & Continue",
      message: (
        <>
          <strong>Please note:</strong> Once you click &quot;Confirm and
          Continue&quot; new screening and prescreening settings will be created
          for this specific property and the portfolio setup will no longer
          apply.
        </>
      ),
      description: veroGoDescription,
      // @ts-ignore
      onConfirm: () => copyPrescreeningDealSetupFromPortfolio(propertyId),
    });
  };

  const openResetSettingsModal = () => {
    if (shouldUseVeroGoReset && hasVeroGoMismatch) {
      return openVeroGoOverrideDialog(openModalDialog, {
        title: "Reset to portfolio setting?",
        subtitle: `${propertyName} in ${portfolioName}`,
        nowState: propertyVeroGoActive ? "on" : "off",
        nowRelationship: "custom",
        afterState: portfolioVeroGoActive ? "on" : "off",
        afterRelationship: "inherited",
        infoText: (
          <>
            This will <strong>re-inherit</strong> settings from {portfolioName}.
            Both screening and pre-screening settings will be updated to match
            the portfolio, and Vero Go will be{" "}
            <strong>{portfolioVeroGoActive ? "enabled" : "disabled"}</strong> on
            this property.
          </>
        ),
        primaryButtonLabel: `Reset to ${portfolioVeroGoActive ? "On" : "Off"}`,
        secondaryButtonLabel: "Keep custom",
        primaryValue: "confirm",
        secondaryValue: "cancel",
      }).then((result: string) => {
        if (result !== "confirm") return undefined;
        return resetPropertyVeroGoToPortfolioSettings(propertyId);
      });
    }

    const veroGoDescription = shouldUseVeroGoReset ? (
      <>
        <strong>Warning:</strong> With Vero Go enabled, resetting pre-screening
        settings here will also reset screening settings to the portfolio.
        {willEnableVeroGoOnReset ? (
          <>
            {" "}
            Vero Go will also be enabled on this property because the portfolio
            is enabled (<strong>On</strong>).
          </>
        ) : null}
      </>
    ) : undefined;

    // @ts-ignore
    return openModalDialog(ConfirmationModal, {
      title: "Reset to portfolio settings?",
      subtitle: (
        <span>
          Portfolio: <strong>{portfolioName}</strong>
        </span>
      ),
      submitButtonLabel: "Reset",
      message: (
        <>
          <strong>Please note:</strong> Once you click &quot;Reset&quot; the
          property screening and prescreening settings will be overridden with
          the current portfolio setup and the changes you previously made will
          no longer apply.
        </>
      ),
      description: veroGoDescription,
      // @ts-ignore
      onConfirm: () => {
        if (shouldUseVeroGoReset) {
          return resetPropertyVeroGoToPortfolioSettings(propertyId);
        }
        return resetToPrescreeningPortfolioSettings(propertyId);
      },
    });
  };

  const onSubmit = isPrescreeningDealSetupFromPortfolio
    ? openCustomizeSettingsModal
    : null;

  const submitButtonLabel = isPrescreeningDealSetupFromPortfolio
    ? "Update settings"
    : "Save Changes";

  const onSecondaryButtonClick = !isPrescreeningDealSetupFromPortfolio
    ? openResetSettingsModal
    : null;

  const settingsSourceIndicator = isPrescreeningDealSetupFromPortfolio
    ? `${portfolioName} settings are active for prequalification`
    : `Custom settings specific to prequalification are applied and refer to ${portfolioName}`;

  return (
    <>
      <Banner Icon={Icon.InfoIcon} message={settingsSourceIndicator} />
      <PrescreeningSettings
        dealSetupId={propertySettings?.prescreeningDealSetupId}
        disabled={isPrescreeningDealSetupFromPortfolio}
        onSubmit={onSubmit}
        submitButtonLabel={submitButtonLabel}
        showSecondaryButton={!isPrescreeningDealSetupFromPortfolio}
        onSecondaryButtonClick={onSecondaryButtonClick}
        secondaryButtonLabel="Reset to portfolio settings"
      />
    </>
  );
};

export default PropertyPrescreeningSettings;
